import ImagePicker from '../components/ImagePicker';
import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import {
  SOCIAL_FEED_QUERY, MY_POSTS_QUERY, POST_QUERY,
  CREATE_POST_MUTATION, LIKE_POST_MUTATION, ADD_POST_COMMENT_MUTATION, DELETE_POST_MUTATION,
} from '../graphql';

const TYPES = [['update', '💬 Update'], ['photo', '📷 Photo'], ['reel', '🎬 Reel'], ['tip', '💡 Tip'], ['article', '📝 Article']];
const typeEmoji = t => ({ update: '💬', photo: '📷', reel: '🎬', tip: '💡', article: '📝' }[t] || '💬');

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (isNaN(s)) return '';
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

function Composer() {
  const [type, setType] = useState('update');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [tags, setTags] = useState('');
  const [showMedia, setShowMedia] = useState(false);
  const [createPost, { loading }] = useMutation(CREATE_POST_MUTATION, {
    refetchQueries: [{ query: SOCIAL_FEED_QUERY }, { query: MY_POSTS_QUERY }],
    onCompleted: () => { setTitle(''); setContent(''); setImageUrl(''); setTags(''); setShowMedia(false); setType('update'); },
    onError: e => alert(e.message),
  });
  const canPost = content.trim() || title.trim() || imageUrl.trim();
  const submit = () => createPost({
    variables: {
      type, title: title.trim() || null, content: content.trim() || null, imageUrl: imageUrl.trim() || null,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean).slice(0, 8),
    },
  });
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {TYPES.map(([id, label]) => (
          <button key={id} className={`btn btn-sm ${type === id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setType(id)}>{label}</button>
        ))}
      </div>
      {(type === 'article' || type === 'tip') && (
        <input className="input" style={{ marginBottom: 8 }} placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
      )}
      <textarea className="input" rows={3} placeholder="Share a hobby moment, a tip, a win…" value={content} onChange={e => setContent(e.target.value)} maxLength={10000} />
      {showMedia && (
        <div style={{ marginTop: 8 }}>
          <ImagePicker
            value={imageUrl} onChange={setImageUrl} preset="post" label=""
            previewWidth={96} previewHeight={96}
            hint="Upload a photo from your device, or paste a link."
          />
        </div>
      )}
      <input className="input" style={{ marginTop: 8 }} placeholder="Tags, comma-separated (e.g. art, beginner)" value={tags} onChange={e => setTags(e.target.value)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowMedia(s => !s)}>{showMedia ? '✕ Remove image' : '🖼️ Add image'}</button>
        <button className="btn btn-primary" disabled={loading || !canPost} onClick={submit}>{loading ? 'Posting…' : '📣 Post'}</button>
      </div>
    </div>
  );
}

function Comments({ postId }) {
  const [text, setText] = useState('');
  const { data, loading, refetch } = useQuery(POST_QUERY, { variables: { id: postId }, fetchPolicy: 'cache-and-network' });
  const [addComment, { loading: adding }] = useMutation(ADD_POST_COMMENT_MUTATION, {
    refetchQueries: [{ query: SOCIAL_FEED_QUERY }],
    onCompleted: () => { setText(''); refetch(); },
    onError: e => alert(e.message),
  });
  const comments = data?.post?.comments || [];
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      {loading && comments.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div> : null}
      {comments.map(c => (
        <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <div className="avatar avatar-sm" style={{ background: c.authorAvatar || 'var(--primary)' }}>{c.authorName?.[0] || '?'}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--font-sm)' }}><b>{c.authorName}</b> <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>· {timeAgo(c.createdAt)}</span></div>
            <div style={{ fontSize: 'var(--font-sm)' }}>{c.content}</div>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input className="input" style={{ flex: 1 }} placeholder="Add a comment…" value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && text.trim()) addComment({ variables: { postId, content: text.trim() } }); }} />
        <button className="btn btn-primary btn-sm" disabled={adding || !text.trim()} onClick={() => addComment({ variables: { postId, content: text.trim() } })}>Send</button>
      </div>
    </div>
  );
}

function PostCard({ post, currentUser }) {
  const [showComments, setShowComments] = useState(false);
  const [like] = useMutation(LIKE_POST_MUTATION, { onError: e => alert(e.message) });
  const [del] = useMutation(DELETE_POST_MUTATION, {
    refetchQueries: [{ query: SOCIAL_FEED_QUERY }, { query: MY_POSTS_QUERY }],
    onError: e => alert(e.message),
  });
  const mine = currentUser && post.authorId === currentUser.id;
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div className="avatar avatar-sm" style={{ background: post.authorAvatar || 'var(--primary)' }}>{post.authorName?.[0] || '?'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 700 }}>{post.authorName}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}> · {typeEmoji(post.type)} {post.type} · {timeAgo(post.createdAt)}</span>
            </div>
            {mine && <button className="btn btn-ghost btn-sm" title="Delete" onClick={() => { if (window.confirm('Delete this post?')) del({ variables: { id: post.id } }); }}>🗑️</button>}
          </div>
          {post.title && <div style={{ fontWeight: 700, fontSize: 'var(--font-lg)', marginTop: 4 }}>{post.title}</div>}
          {post.content && <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{post.content}</div>}
          {post.imageUrl && <img src={post.imageUrl} alt="" style={{ marginTop: 8, maxWidth: '100%', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }} onError={e => { e.target.style.display = 'none'; }} />}
          {post.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {post.tags.map(t => <span key={t} className="badge" style={{ background: 'var(--primary-light)', color: 'var(--primary-hover)' }}>#{t}</span>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => like({ variables: { id: post.id } })} style={{ color: post.likedByMe ? 'var(--primary)' : undefined }}>
              {post.likedByMe ? '❤️' : '🤍'} {post.likeCount || 0}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowComments(s => !s)}>💬 {post.commentCount || 0}</button>
          </div>
          {showComments && <Comments postId={post.id} />}
        </div>
      </div>
    </div>
  );
}

export default function SocialPage({ onAuthRequired }) {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('feed');
  const { data: feedData, loading: feedLoading } = useQuery(SOCIAL_FEED_QUERY, { skip: !currentUser, fetchPolicy: 'cache-and-network' });
  const { data: myData } = useQuery(MY_POSTS_QUERY, { skip: !currentUser, fetchPolicy: 'cache-and-network' });

  if (!currentUser) return (
    <div className="page"><div className="container" style={{ paddingTop: 100, textAlign: 'center' }}>
      <p style={{ marginBottom: 16 }}>Sign in to see the community feed and share your hobby moments.</p>
      <button className="btn btn-primary" onClick={() => onAuthRequired?.('login')}>Sign in</button>
    </div></div>
  );

  const feed = feedData?.socialFeed || [];
  const mine = myData?.myPosts || [];

  return (
    <div className="page">
      <div className="container" style={{ padding: '32px 16px', maxWidth: 640 }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 4 }}>✨ HobbySocial</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>Share hobby moments, reels and tips with the community — like, comment and get inspired.</p>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {[['feed', '🌊 Feed'], ['mine', `📋 My posts${mine.length ? ` (${mine.length})` : ''}`]].map(([id, label]) => (
            <button key={id} className={`btn btn-sm ${tab === id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        {tab === 'feed' && (
          <>
            <Composer />
            {feedLoading && feed.length === 0 ? <div className="spinner" /> : feed.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No posts yet — be the first to share something! ✨</div>
            ) : feed.map(p => <PostCard key={p.id} post={p} currentUser={currentUser} />)}
          </>
        )}

        {tab === 'mine' && (
          mine.length === 0 ? <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>You haven't posted anything yet.</div>
            : mine.map(p => <PostCard key={p.id} post={p} currentUser={currentUser} />)
        )}
      </div>
    </div>
  );
}
